import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ShieldCheck, Plus, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IPWhitelistPage = () => {
  const [settings, setSettings] = useState({
    allowed_ips: [],
    blocked_ips: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newAllowedIP, setNewAllowedIP] = useState('');
  const [newBlockedIP, setNewBlockedIP] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ip-whitelist`);
      setSettings({
        allowed_ips: data.allowed_ips || [],
        blocked_ips: data.blocked_ips || []
      });
    } catch (e) {
      console.error('Failed to fetch IP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/ip-whitelist`, settings);
      toast.success('IP whitelist saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addAllowedIP = () => {
    if (!newAllowedIP) return;
    if (settings.allowed_ips.includes(newAllowedIP)) {
      toast.error('IP already in list');
      return;
    }
    setSettings(prev => ({
      ...prev,
      allowed_ips: [...prev.allowed_ips, newAllowedIP]
    }));
    setNewAllowedIP('');
  };

  const removeAllowedIP = (ip) => {
    setSettings(prev => ({
      ...prev,
      allowed_ips: prev.allowed_ips.filter(i => i !== ip)
    }));
  };

  const addBlockedIP = () => {
    if (!newBlockedIP) return;
    if (settings.blocked_ips.includes(newBlockedIP)) {
      toast.error('IP already in list');
      return;
    }
    setSettings(prev => ({
      ...prev,
      blocked_ips: [...prev.blocked_ips, newBlockedIP]
    }));
    setNewBlockedIP('');
  };

  const removeBlockedIP = (ip) => {
    setSettings(prev => ({
      ...prev,
      blocked_ips: prev.blocked_ips.filter(i => i !== ip)
    }));
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">IP Whitelist</h1>
        <p className="text-muted-foreground">Control access to temporary links by IP address</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck size={20} className="text-green-600" />
              Allowed IPs
            </CardTitle>
            <CardDescription>
              IPs that can access temporary links (if specified on link)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newAllowedIP}
                onChange={(e) => setNewAllowedIP(e.target.value)}
                placeholder="192.168.1.1"
                data-testid="allowed-ip-input"
              />
              <Button variant="outline" onClick={addAllowedIP} data-testid="add-allowed-ip">
                <Plus size={16} />
              </Button>
            </div>
            {settings.allowed_ips.length > 0 ? (
              <div className="space-y-2">
                {settings.allowed_ips.map(ip => (
                  <div key={ip} className="flex items-center justify-between p-2 bg-green-50 rounded-md">
                    <span className="font-mono text-sm">{ip}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAllowedIP(ip)}
                      className="h-6 w-6 text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No allowed IPs configured
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck size={20} className="text-destructive" />
              Blocked IPs
            </CardTitle>
            <CardDescription>
              IPs that are always blocked from accessing temporary links
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={newBlockedIP}
                onChange={(e) => setNewBlockedIP(e.target.value)}
                placeholder="10.0.0.1"
                data-testid="blocked-ip-input"
              />
              <Button variant="outline" onClick={addBlockedIP} data-testid="add-blocked-ip">
                <Plus size={16} />
              </Button>
            </div>
            {settings.blocked_ips.length > 0 ? (
              <div className="space-y-2">
                {settings.blocked_ips.map(ip => (
                  <div key={ip} className="flex items-center justify-between p-2 bg-red-50 rounded-md">
                    <span className="font-mono text-sm">{ip}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBlockedIP(ip)}
                      className="h-6 w-6 text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No blocked IPs configured
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} data-testid="save-ip-settings">
          {saving ? 'Saving...' : 'Save IP Settings'}
        </Button>
      </div>
    </div>
  );
};

export default IPWhitelistPage;
